import {
  brandIds,
  defaultLocales,
  environmentVariables,
  subdomainLocales,
} from '~/assets/data/config';
import { globalKeys } from 'assets/data/global-keys';
import type { IEnvironment } from '~/interfaces/states/environment.state';

export default function useFetchEnvironment(): IEnvironment {
  const { $storageService } = useServices();
  const { getLocale, setLocale } = useLocaleUtils();
  const runtimeConfig = useRuntimeConfig();
  const siteStore = useSiteStore();
  const route = useRoute();
  const viewport = useViewport();

  const host = useRequestURL().host.split(':')[0];

  // url tests
  const testHost = computed(() => !!host.match(/uat|dev|qa|sandbox/));
  const localhost = computed(
    () => !!useRequestURL().host.match(/localhost|:3000|:4500/),
  );
  const production = computed(() => !!host.match(/www|new|en|beta|alpha/));
  const localeSubdomainHost = computed(() => host.match(/en|pt|sw/));

  // storage
  const debugRegionCode = $storageService.getLocalStorage<string>({
    key: globalKeys.developerConfigKeys.debugRegion,
  });
  const debugEnvironment = $storageService.getLocalStorage<string>({
    key: globalKeys.developerConfigKeys.debugEnvironment,
  });
  const localeCookie: string = $storageService.getCookie({
    key: globalKeys.siteConfigKeys.locale,
  });

  const currentBuildTimeStamp = $storageService.getLocalStorage<string>({
    key: globalKeys.siteConfigKeys.buildTimestamp,
  });

  // configurations
  const latestBuildTimestamp = runtimeConfig?.public.buildTimestamp;
  const isLatestBuild = +currentBuildTimeStamp !== +latestBuildTimestamp;

  $storageService.setLocalStorage({
    key: globalKeys.siteConfigKeys.buildTimestamp,
    data: runtimeConfig?.public.buildTimestamp,
  });

  if (!!debugEnvironment && (testHost.value || localhost.value)) {
    siteStore.setEnvironment(debugEnvironment);
    Object.assign(
      runtimeConfig?.public,
      environmentVariables[debugEnvironment],
    );
  } else if (!production.value) {
    siteStore.setEnvironment(runtimeConfig.public.environment);
    Object.assign(
      runtimeConfig?.public,
      environmentVariables[runtimeConfig.public.environment],
    );
  }

  const countryCode = localhost.value
    ? debugRegionCode || runtimeConfig?.public.defaultCountry
    : host?.slice(-2);

  const regionCode = `J${countryCode.toUpperCase()}`;

  const presetLocale =
    testHost.value && !!route.query?.locale
      ? String(route.query?.locale)
      : !!localeSubdomainHost.value
        ? subdomainLocales[localeSubdomainHost.value[0]]
        : !!localeCookie && (testHost.value || localhost.value)
          ? localeCookie
          : defaultLocales[`J${countryCode.toUpperCase()}`];

  if (!!presetLocale) setLocale(presetLocale);

  $storageService.setLocalStorage({
    key: globalKeys.siteConfigKeys.environment,
    data: runtimeConfig?.public.environment,
  });

  return {
    host,
    environment: {
      testHost: testHost.value,
      localhost: localhost.value,
      localeSubdomainHost: localeSubdomainHost.value,
      isLatestBuild,
      variable: runtimeConfig.public.environment,
      production: production.value,
    },
    storageConfigs: { debugRegionCode, debugEnvironment, localeCookie },
    regionConfigs: {
      countryCode,
      regionCode,
      brandId: brandIds[regionCode],
      presetLocale,
      currentLocale: getLocale(),
      runtimeEnvironment: runtimeConfig?.public.environment,
      isGlobal:
        document.location.hostname.includes('jackpotcitycasino') ||
        String(runtimeConfig.public.defaultGlobalBranding) === 'true',
    },
    deviceConfig: {
      userAgent: window.navigator.userAgent,
      channel: viewport.isGreaterThan('tablet') ? 'WebDesktop' : 'WebMobile',
    },
  };
}
